<template>
  <div class="poll-card" data-cy="poll-card">
    <div class="poll-card__header">
      <span class="poll-card__icon" aria-hidden="true">
        <i class="fa-solid fa-chart-column"></i>
      </span>
      <div class="poll-card__heading">
        <p class="poll-card__question">{{ localPoll.question }}</p>
        <p v-if="localPoll.allow_multiple" class="poll-card__hint">Select all that apply</p>
      </div>
      <span v-if="isClosed" class="poll-card__status-pill" data-cy="poll-closed-pill">Closed</span>
    </div>

    <div class="poll-card__options">
      <button
        v-for="option in localPoll.poll_options"
        :key="option.id"
        type="button"
        class="poll-option"
        data-cy="poll-option"
        :class="{ 'poll-option-selected': isSelected(option.id), 'poll-option-disabled': isClosed }"
        :disabled="isClosed"
        @click="handleVote(option)"
      >
        <div class="poll-bar" :style="{ width: getPercentage(option) + '%' }"></div>
        <div class="poll-option__row">
          <div class="poll-option__label">
            <span v-if="isSelected(option.id)" class="poll-option__check" aria-hidden="true">&#10003;</span>
            <span>{{ option.text }}</span>
          </div>
          <div class="poll-option__stats">
            <span class="poll-option__percent">{{ getPercentage(option) }}%</span>
            <span class="poll-option__count">{{ option.votes_count }} {{ option.votes_count === 1 ? 'vote' : 'votes' }}</span>
          </div>
        </div>
      </button>
    </div>

    <div class="poll-card__footer">
      <span class="poll-card__total" data-cy="poll-total">{{ localPoll.votes_count }} total {{ localPoll.votes_count === 1 ? 'vote' : 'votes' }}</span>
      <a v-if="canClose && !isClosed" class="poll-card__action" data-cy="poll-close" @click.prevent="closePoll">Close poll</a>
      <a v-if="canClose && isClosed" class="poll-card__action" data-cy="poll-reopen" @click.prevent="reopenPoll">Reopen poll</a>
    </div>

    <div v-if="isCoach" class="poll-card__coach-row">
      <a v-if="!showResults" class="poll-card__action" data-cy="poll-results-link" @click.prevent="loadResults">See results</a>
      <div v-else class="poll-card__results" data-cy="poll-results">
        <p v-if="loadingResults" class="poll-card__nonvoters-body">Loading...</p>
        <template v-else>
          <div v-for="option in localPoll.poll_options" :key="`voters-${option.id}`" class="poll-card__results-group">
            <p class="poll-card__nonvoters-title">{{ option.text }}</p>
            <p class="poll-card__nonvoters-body">
              <template v-if="votersByOption[option.id] && votersByOption[option.id].length > 0">
                <span v-for="(vote, idx) in votersByOption[option.id]" :key="vote.id">{{ vote.profile.display_name }}<span v-if="idx < votersByOption[option.id].length - 1">, </span></span>
              </template>
              <template v-else>No votes yet</template>
            </p>
          </div>
          <div class="poll-card__results-group">
            <p class="poll-card__nonvoters-title">Haven't voted yet:</p>
            <p v-if="nonVoters && nonVoters.length === 0" class="poll-card__nonvoters-body">Everyone has voted!</p>
            <p v-else-if="nonVoters" class="poll-card__nonvoters-body">
              <span v-for="(nv, idx) in nonVoters" :key="nv.id">{{ nv.display_name }}<span v-if="idx < nonVoters.length - 1">, </span></span>
            </p>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'poll-card',
  props: {
    poll: {
      type: Object,
      required: true,
    },
    messageId: {
      type: Number,
      required: true,
    },
    activeProfileId: {
      type: Number,
      required: true,
    },
    activeProfileType: {
      type: String,
      required: true,
    },
    isCoach: {
      type: Boolean,
      default: false,
    },
    messageAuthorId: {
      type: Number,
      default: 0,
    },
    messageAuthorType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      localPoll: JSON.parse(JSON.stringify(this.poll)),
      voting: false,
      showResults: false,
      loadingResults: false,
      votes: null,
      nonVoters: null,
    };
  },
  computed: {
    isClosed() {
      return this.localPoll.closed_at !== null;
    },
    currentUserVoteOptionIds() {
      return (this.localPoll.current_user_votes || []).map((v) => v.poll_option_id);
    },
    canClose() {
      const isCreator = this.messageAuthorId === this.activeProfileId
        && this.messageAuthorType === this.activeProfileType;
      return isCreator || this.isCoach;
    },
    votersByOption() {
      const grouped = {};
      (this.votes || []).forEach((vote) => {
        if (!grouped[vote.poll_option_id]) grouped[vote.poll_option_id] = [];
        grouped[vote.poll_option_id].push(vote);
      });
      return grouped;
    },
  },
  watch: {
    poll: {
      handler(newPoll) {
        this.localPoll = JSON.parse(JSON.stringify(newPoll));
      },
      deep: true,
    },
  },
  methods: {
    isSelected(optionId) {
      return this.currentUserVoteOptionIds.includes(optionId);
    },
    getPercentage(option) {
      if (this.localPoll.votes_count === 0) return 0;
      return Math.round((option.votes_count / this.localPoll.votes_count) * 100);
    },
    handleVote(option) {
      if (this.isClosed || this.voting) return;

      if (this.isSelected(option.id)) {
        this.removeVote(option);
      } else {
        this.castVote(option);
      }
    },
    castVote(option) {
      const vm = this;
      vm.voting = true;

      // Optimistic update
      const previousVotes = JSON.parse(JSON.stringify(vm.localPoll.current_user_votes));
      const previousOptions = JSON.parse(JSON.stringify(vm.localPoll.poll_options));
      const previousTotalVotes = vm.localPoll.votes_count;

      if (!vm.localPoll.allow_multiple) {
        // Single-select: remove previous selection optimistically
        vm.localPoll.current_user_votes.forEach((v) => {
          const prevOpt = vm.localPoll.poll_options.find((o) => o.id === v.poll_option_id);
          if (prevOpt) prevOpt.votes_count -= 1;
          vm.localPoll.votes_count -= 1;
        });
        vm.localPoll.current_user_votes = [];
      }

      // Add new vote optimistically
      const opt = vm.localPoll.poll_options.find((o) => o.id === option.id);
      if (opt) opt.votes_count += 1;
      vm.localPoll.votes_count += 1;
      vm.localPoll.current_user_votes.push({ id: -1, poll_option_id: option.id });

      const url = vm.$apiService.pollVotesUrl(vm.localPoll.id);
      axios.post(url, { poll_vote: { poll_option_id: option.id } })
        .then((response) => {
          // Update the temporary vote with the real ID
          const tempVote = vm.localPoll.current_user_votes.find((v) => v.id === -1 && v.poll_option_id === option.id);
          if (tempVote) tempVote.id = response.data.id;
          vm.voting = false;
          if (vm.showResults) vm.fetchResults();
        })
        .catch(() => {
          // Rollback
          vm.localPoll.current_user_votes = previousVotes;
          vm.localPoll.poll_options = previousOptions;
          vm.localPoll.votes_count = previousTotalVotes;
          vm.voting = false;
          vm.$notificationManager.emit('show-toast', 'Failed to vote', false);
        });
    },
    removeVote(option) {
      const vm = this;
      const vote = vm.localPoll.current_user_votes.find((v) => v.poll_option_id === option.id);
      if (!vote) return;

      vm.voting = true;

      // Optimistic update
      const previousVotes = JSON.parse(JSON.stringify(vm.localPoll.current_user_votes));
      const previousOptions = JSON.parse(JSON.stringify(vm.localPoll.poll_options));
      const previousTotalVotes = vm.localPoll.votes_count;

      vm.localPoll.current_user_votes = vm.localPoll.current_user_votes.filter((v) => v.poll_option_id !== option.id);
      const opt = vm.localPoll.poll_options.find((o) => o.id === option.id);
      if (opt) opt.votes_count -= 1;
      vm.localPoll.votes_count -= 1;

      const url = vm.$apiService.pollVoteUrl(vm.localPoll.id, vote.id);
      axios.delete(url)
        .then(() => {
          vm.voting = false;
          if (vm.showResults) vm.fetchResults();
        })
        .catch(() => {
          // Rollback
          vm.localPoll.current_user_votes = previousVotes;
          vm.localPoll.poll_options = previousOptions;
          vm.localPoll.votes_count = previousTotalVotes;
          vm.voting = false;
          vm.$notificationManager.emit('show-toast', 'Failed to remove vote', false);
        });
    },
    closePoll() {
      const vm = this;
      const url = vm.$apiService.pollCloseUrl(vm.localPoll.id);
      axios.put(url)
        .then((response) => {
          vm.localPoll.closed_at = response.data.closed_at;
          vm.$notificationManager.emit('show-toast', 'Poll closed', true);
        })
        .catch(() => {
          vm.$notificationManager.emit('show-toast', 'Failed to close poll', false);
        });
    },
    reopenPoll() {
      const vm = this;
      const url = vm.$apiService.pollReopenUrl(vm.localPoll.id);
      axios.put(url)
        .then((response) => {
          vm.localPoll.closed_at = response.data.closed_at;
          vm.$notificationManager.emit('show-toast', 'Poll reopened', true);
        })
        .catch(() => {
          vm.$notificationManager.emit('show-toast', 'Failed to reopen poll', false);
        });
    },
    loadResults() {
      this.showResults = true;
      this.loadingResults = true;
      this.fetchResults();
    },
    fetchResults() {
      const vm = this;
      const votersRequest = axios.get(vm.$apiService.pollVotersUrl(vm.localPoll.id));
      const nonVotersRequest = axios.get(vm.$apiService.pollNonVotersUrl(vm.localPoll.id));
      Promise.all([votersRequest, nonVotersRequest])
        .then(([votersResponse, nonVotersResponse]) => {
          vm.votes = votersResponse.data.votes;
          vm.nonVoters = nonVotersResponse.data.non_voters;
          vm.loadingResults = false;
        })
        .catch(() => {
          vm.loadingResults = false;
          vm.$notificationManager.emit('show-toast', 'Failed to load results', false);
        });
    },
  },
};
</script>
